import React, { useCallback, useMemo } from 'react'

import { ActionsProvider } from '../../../../providers/ActionsProvider/ActionsProvider'
import { TooltipProvider } from '../../../../providers/TooltipProvider/TooltipProvider'
import { useModal } from '../../../../providers/ModalProvider/ModalProvider'
import { Confirm, Error } from '../../../ModalContent'
import { SeatMap } from '../../../SeatMap'
import {
  getVenueSeatScale,
  isNmsuStyleSeatmapVenue,
} from '../../../../utilities/frontendSeatmapSizing'

const normalizeSeatId = (seatId) => String(seatId ?? '').trim()

const getSeatMapValue = (seatMap, seatId) => {
  if (!seatMap) return undefined
  const normalizedSeatId = normalizeSeatId(seatId)
  return seatMap.get(seatId) || seatMap.get(normalizedSeatId)
}

const getHoldLabel = (holdInfo) => {
  if (!holdInfo || holdInfo === true) return null
  if (holdInfo.categoryName && holdInfo.holdName) {
    return `${holdInfo.holdName} (${holdInfo.categoryName})`
  }
  return holdInfo.holdName || holdInfo.categoryName || null
}

const formatHoldNames = (holdLabels) => {
  const uniqueLabels = [...new Set(holdLabels.filter(Boolean))]
  if (uniqueLabels.length === 0) return null
  if (uniqueLabels.length === 1) return uniqueLabels[0]
  if (uniqueLabels.length === 2)
    return `${uniqueLabels[0]} and ${uniqueLabels[1]}`
  return `${uniqueLabels.slice(0, -1).join(', ')}, and ${uniqueLabels.at(-1)}`
}

export default function PackageMap({
  data,
  background,
  seatsInPackage,
  selectedSeatIds,
  setSelectedSeatIds,
  isMapDisabled,
  isAssigningToFan,
  soldMap,
  reservedMap,
  holdMap,
  openGAModal,
  eventPackage,
}) {
  const { openModal, closeModal } = useModal()

  const SELECTED_COLOR = '#141416' // black - selecting seats on map
  const SOLD_COLOR = '#45B26B' // green for sold
  const INCLUDED_COLOR = '#FFA500' // orange color of seats included in package
  const HOLD_COLOR = '#EF466F'
  const DEFAULT_COLOR = '#c6c8cc'
  const RESERVED_COLOR = '#ee00f3'
  const venue =
    eventPackage?.venue ||
    eventPackage?.events?.[0]?.venue ||
    eventPackage?.event?.venue
  const venueSlug =
    venue?.slug || eventPackage?.venueSlug || eventPackage?.venue_slug
  const venueName =
    venue?.name || eventPackage?.venueName || eventPackage?.venue_name
  const useFrontendSeatSizing = isNmsuStyleSeatmapVenue(venueSlug)
  const seatScale = getVenueSeatScale({ venueName, venueSlug })

  const packageTicketStatusBySeat = useMemo(() => {
    const ticketsBySeat = new Map()

    eventPackage?.package_tickets?.forEach((ticket) => {
      const seatId = normalizeSeatId(ticket?.seatId ?? ticket?.seat_id)
      if (!seatId) return

      if (!ticketsBySeat.has(seatId)) {
        ticketsBySeat.set(seatId, [])
      }
      ticketsBySeat.get(seatId).push(ticket)
    })

    const statusBySeat = new Map()
    for (const [seatId, tickets] of ticketsBySeat.entries()) {
      const hasAvailable = tickets.some(
        (ticket) => ticket.on_sale_status === 'available'
      )
      if (hasAvailable) continue

      const hasReserved = tickets.some(
        (ticket) => ticket.on_sale_status === 'reserved'
      )
      if (hasReserved) {
        statusBySeat.set(seatId, 'reserved')
        continue
      }

      const hasSold = tickets.some((ticket) => ticket.on_sale_status === 'sold')
      if (hasSold) {
        statusBySeat.set(seatId, 'sold')
      }
    }

    return statusBySeat
  }, [eventPackage?.package_tickets])

  const isSeatUnavailable = useCallback(
    (seatId) => {
      const normalizedSeatId = normalizeSeatId(seatId)
      const packageTicketStatus =
        packageTicketStatusBySeat.get(normalizedSeatId)

      return (
        Boolean(getSeatMapValue(reservedMap, seatId)) ||
        packageTicketStatus === 'reserved' ||
        Boolean(getSeatMapValue(soldMap, seatId)) ||
        packageTicketStatus === 'sold'
      )
    },
    [packageTicketStatusBySeat, reservedMap, soldMap]
  )

  const isSeatSelected = useCallback(
    (seatId) => {
      const normalizedSeatId = normalizeSeatId(seatId)
      return Boolean(
        selectedSeatIds?.seats &&
        (selectedSeatIds.seats[normalizedSeatId] === normalizedSeatId ||
          selectedSeatIds.seats[seatId] === seatId)
      )
    },
    [selectedSeatIds]
  )

  const isSeatInPackage = useCallback(
    (seatId) => {
      const normalizedSeatId = normalizeSeatId(seatId)
      return Boolean(
        seatsInPackage?.seats &&
        (seatsInPackage.seats[normalizedSeatId] === normalizedSeatId ||
          seatsInPackage.seats[seatId] === seatId)
      )
    },
    [seatsInPackage]
  )

  // for sold seats return sold color + disabled
  const getSeatState = useCallback(
    (seatId) => {
      const normalizedSeatId = normalizeSeatId(seatId)
      const packageTicketStatus =
        packageTicketStatusBySeat.get(normalizedSeatId)

      if (
        getSeatMapValue(reservedMap, seatId) ||
        packageTicketStatus === 'reserved'
      ) {
        return { disabled: true, color: RESERVED_COLOR }
      }

      if (getSeatMapValue(soldMap, seatId) || packageTicketStatus === 'sold') {
        return { disabled: true, color: SOLD_COLOR }
      }

      if (
        selectedSeatIds?.seats &&
        (selectedSeatIds.seats[normalizedSeatId] === normalizedSeatId ||
          selectedSeatIds.seats[seatId] === seatId)
      ) {
        return { disabled: false, color: SELECTED_COLOR }
      }

      if (getSeatMapValue(holdMap, seatId)) {
        // When assigning tickets, held package seats stay clickable (with confirmation).
        // Held seats outside this package can't be assigned here, and holds remain
        // blocked while configuring package inventory.
        const inPackage = Boolean(
          seatsInPackage?.seats &&
          (seatsInPackage.seats[normalizedSeatId] === normalizedSeatId ||
            seatsInPackage.seats[seatId] === seatId)
        )
        return { disabled: !(isAssigningToFan && inPackage), color: HOLD_COLOR }
      }

      if (
        seatsInPackage?.seats &&
        (seatsInPackage.seats[normalizedSeatId] === normalizedSeatId ||
          seatsInPackage.seats[seatId] === seatId)
      ) {
        return { disabled: false, color: INCLUDED_COLOR }
      }

      // Disable the rest of the seats if isAssigningToFan
      return { disabled: isAssigningToFan, color: DEFAULT_COLOR }
    },
    [
      packageTicketStatusBySeat,
      reservedMap,
      soldMap,
      holdMap,
      selectedSeatIds,
      seatsInPackage,
      isAssigningToFan,
    ]
  )

  const getSeatTooltipDetails = useCallback(
    (seatId) => {
      const seatIdentity =
        getSeatMapValue(reservedMap, seatId) || getSeatMapValue(soldMap, seatId)
      if (seatIdentity && seatIdentity !== true) return seatIdentity

      const holdInfo = getSeatMapValue(holdMap, seatId)
      const holdLabel = getHoldLabel(holdInfo)
      if (holdLabel) {
        return { holdName: holdLabel }
      }

      return null
    },
    [reservedMap, soldMap, holdMap]
  )

  const applySeatSelection = useCallback(
    (seatIds, { forceSelect = false } = {}) => {
      setSelectedSeatIds((prevSelected) => {
        const newSelected = { ...prevSelected }
        if (!newSelected.seats) {
          newSelected.seats = {}
        }

        seatIds.forEach((id) => {
          const normalizedId = normalizeSeatId(id)
          if (!normalizedId || isSeatUnavailable(id)) return

          // When assigning to a fan, only package seats can be selected
          if (isAssigningToFan && !isSeatInPackage(id)) return

          const currentlySelected =
            newSelected.seats[normalizedId] === normalizedId
          if (forceSelect) {
            newSelected.seats[normalizedId] = normalizedId
            return
          }

          if (currentlySelected) {
            delete newSelected.seats[normalizedId]
          } else {
            newSelected.seats[normalizedId] = normalizedId
          }
        })

        if (Object.keys(newSelected.seats).length === 0) {
          delete newSelected.seats
        }

        return newSelected
      })
    },
    [isAssigningToFan, isSeatInPackage, isSeatUnavailable, setSelectedSeatIds]
  )

  const confirmHeldSeatSelection = useCallback(
    (seatIds, holdLabels) => {
      const holdNames = formatHoldNames(holdLabels)
      const isSingle = seatIds.length === 1
      const description = holdNames
        ? isSingle
          ? `This seat is on hold for "${holdNames}". Would you like to continue and select it for assignment?`
          : `Some of these seats are on hold for "${holdNames}". Would you like to continue and select them for assignment?`
        : isSingle
          ? 'This seat is currently on hold. Would you like to continue and select it for assignment?'
          : 'Some of these seats are currently on hold. Would you like to continue and select them for assignment?'

      openModal(
        Confirm,
        {
          title: isSingle ? 'Seat on hold' : 'Seats on hold',
          description,
          confirmLabel: 'Yes',
          onConfirm: () => {
            applySeatSelection(seatIds, { forceSelect: true })
            closeModal()
          },
          onClose: closeModal,
        },
        { className: 'modal-md', contentClassName: '' }
      )
    },
    [applySeatSelection, closeModal, openModal]
  )

  const explainUnassignableHold = useCallback(
    (holdLabels) => {
      const holdNames = formatHoldNames(holdLabels)

      openModal(
        Error,
        {
          title: 'Seat not available in this package',
          description: holdNames
            ? `This seat is on hold for "${holdNames}" and is not part of this package, so it can't be assigned here.`
            : "This seat is on hold and is not part of this package, so it can't be assigned here.",
          onClose: closeModal,
        },
        { className: 'modal-md', contentClassName: '' }
      )
    },
    [closeModal, openModal]
  )

  // save regular seated seats,,,
  const selectRegularSeats = useCallback(
    (seatId, seatIdArray) => {
      // if map is disabled don't select seats
      if (isMapDisabled) return

      const seatIds = seatId ? [seatId] : seatIdArray || []
      const seatsToToggle = []
      const heldSeatsToAdd = []
      const holdLabels = []
      const unassignableHoldLabels = []

      seatIds.forEach((id) => {
        const normalizedId = normalizeSeatId(id)
        if (!normalizedId || isSeatUnavailable(id)) return

        const holdInfo = getSeatMapValue(holdMap, id)
        const inPackage = isSeatInPackage(id)

        if (holdInfo && isAssigningToFan && !inPackage) {
          unassignableHoldLabels.push(getHoldLabel(holdInfo))
          return
        }

        // Outside package seats stay non-selectable while assigning
        if (isAssigningToFan && !inPackage) return

        if (isSeatSelected(id)) {
          seatsToToggle.push(normalizedId)
          return
        }

        if (holdInfo) {
          // Package config keeps holds blocked; assignment asks for confirmation
          if (!isAssigningToFan) return

          heldSeatsToAdd.push(normalizedId)
          holdLabels.push(getHoldLabel(holdInfo))
          return
        }

        if (getSeatState(id).disabled) return

        seatsToToggle.push(normalizedId)
      })

      if (seatsToToggle.length > 0) {
        applySeatSelection(seatsToToggle)
      }

      if (heldSeatsToAdd.length > 0) {
        confirmHeldSeatSelection(heldSeatsToAdd, holdLabels)
        return
      }

      if (seatsToToggle.length === 0 && unassignableHoldLabels.length > 0) {
        explainUnassignableHold(unassignableHoldLabels)
      }
    },
    [
      applySeatSelection,
      confirmHeldSeatSelection,
      explainUnassignableHold,
      getSeatState,
      holdMap,
      isAssigningToFan,
      isMapDisabled,
      isSeatInPackage,
      isSeatSelected,
      isSeatUnavailable,
    ]
  )

  // GA sections use this to get their color
  const getSectionState = (section) => {
    if (
      selectedSeatIds &&
      selectedSeatIds.gaSeats &&
      selectedSeatIds.gaSeats.hasOwnProperty(section.sectionId)
    ) {
      return { disabled: false, color: SELECTED_COLOR }
    }

    if (
      seatsInPackage?.gaSeats &&
      seatsInPackage.gaSeats.hasOwnProperty(section.sectionId)
    ) {
      if (isAssigningToFan) {
        const normalizedSectionId = normalizeSeatId(section.sectionId)
        const hasAvailableInventory = (
          eventPackage?.package_tickets || []
        ).some((ticket) => {
          const isGa = Boolean(ticket?.GA ?? ticket?.ga)
          const ticketSectionId = normalizeSeatId(
            ticket?.sectionId ?? ticket?.section_id
          )
          return (
            isGa &&
            ticketSectionId === normalizedSectionId &&
            ticket?.on_sale_status === 'available'
          )
        })
        if (!hasAvailableInventory) {
          // Still clickable so the modal can explain there is no inventory.
          return { disabled: false, color: SOLD_COLOR }
        }
      }
      return { disabled: false, color: INCLUDED_COLOR }
    }

    // Disable the rest of the sections if isAssigningToFan
    return { disabled: isAssigningToFan, color: DEFAULT_COLOR }
  }

  // This is triggered for BOTH GA and seated:
  // ( since you can select seated by selecting the entire section )
  const handleSectionSelect = (section) => {
    // if map is disabled don't select sections
    if (isMapDisabled) return

    if (section?.zoomable) {
      // Seated-section
      const seatIds = getAllSeatsInSeatedSection(section, data.rows, data.seats)
      selectRegularSeats(undefined, seatIds)
    } else {
      // GA-section
      openGAModal(section)
    }
  }

  const getAllSeatsInSeatedSection = (section, rows, seats) => {
    return Object.values(rows)
      .filter((row) => row.sectionId === section.sectionId)
      .flatMap((row) => row.seats)
      .filter((seatId) => {
        if (!seats[seatId]) return false
        return true
      })
  }

  const legendItems = isAssigningToFan
    ? [
        { label: 'Sold', className: 'sold' },
        { label: 'Reserved', className: 'reserved' },
        { label: 'Selectable', className: 'selectable' },
        { label: 'Selected', className: 'selected' },
        { label: 'Hold', className: 'hold' },
        { label: 'Unselected', className: 'unselected' },
      ]
    : [
        { label: 'Sold', className: 'sold' },
        { label: 'Selected', className: 'selected' },
        { label: 'Hold', className: 'hold' },
        { label: 'Unselected', className: 'unselected' },
      ]

  /*
    Customize this file ( or the files above ) in any which way that suits your purposes
    Just DO-NOT customize anything lower than this
  */

  const inventoryInfoBasedOnMapType = isAssigningToFan
    ? { displayInventoryInfo: false, deriveInventoryInfoFrom: [] }
    : {
        displayInventoryInfo: true,
        deriveInventoryInfoFrom: [
          {
            inventory: [{ ...selectedSeatIds, color: SELECTED_COLOR }],
            type: 'offers',
          },
        ],
      }

  return (
    <div style={{ width: '100%', height: '100vh', overflow: 'hidden' }}>
      {/* Seatmap fills all available space, so its also behind the Navigation now */}
      <TooltipProvider>
        <ActionsProvider canEdit={!isMapDisabled} legendItems={legendItems}>
          <SeatMap
            data={data}
            background={background}
            isMapDisabled={isMapDisabled}
            useFrontendSeatSizing={useFrontendSeatSizing}
            seatScale={seatScale}
            // Handling clicks
            selectRegularSeats={selectRegularSeats}
            handleSectionSelect={handleSectionSelect}
            // Fill Colors
            getSectionState={getSectionState}
            getSeatState={getSeatState}
            getSeatTooltipDetails={getSeatTooltipDetails}
            // For inventory "bar"
            displayInventoryInfo={
              inventoryInfoBasedOnMapType.displayInventoryInfo
            }
            deriveInventoryInfoFrom={
              inventoryInfoBasedOnMapType.deriveInventoryInfoFrom
            }
          />
        </ActionsProvider>
      </TooltipProvider>
    </div>
  )
}
